import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';

const STORAGE_KEY = 'pulse_studio_companion_dismissed';

/**
 * Lightweight contextual tips — additive, dismissible. Does not remove any existing UI.
 */
export function StudioCompanionBubble({
  visible = true,
  stage = 'daw', // 'daw' | 'stems' | 'dj'
  hasStems = false,
}) {
  const { lang } = useLang();
  const es = lang === 'es';
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!visible) return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') setDismissed(true);
    } catch {}
  }, [visible]);

  if (!visible || dismissed) return null;

  const tips = {
    daw: es
      ? 'Abajo tienes la pista; arriba el chat. Usa el candado en cada capa para que PULSE no la regenere sin permiso.'
      : 'Your timeline is below; chat is on the side. Lock each layer so PULSE won’t regenerate it without permission.',
    stems: es
      ? 'Modo pistas: mute/solo, volumen, y candado. Desbloquea solo lo que quieres que cambie.'
      : 'Stem view: mute/solo, level, and lock. Unlock only what you want changed.',
    dj: es
      ? 'Cabina: EQ y efectos en vivo sobre lo que suena. No sustituye al chat: lo complementa.'
      : 'DJ strip: live EQ and FX on what’s playing. It complements chat — doesn’t replace it.',
  };

  const line = hasStems && stage === 'daw' ? tips.stems : tips[stage] || tips.daw;

  const dismiss = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="pointer-events-auto flex items-start gap-2 rounded-xl border border-[#F5C84035] bg-[#0F0F12]/95 px-3 py-2 shadow-lg backdrop-blur-sm max-w-[min(100%,320px)]"
      data-testid="studio-companion-bubble"
    >
      <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#F5C842] to-[#FF6B00]">
        <Sparkles size={14} className="text-black" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#F5C842]">
          {es ? 'PULSE' : 'PULSE'}
        </p>
        <p className="text-[11px] leading-snug text-[#A1A1AA]">{line}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="flex-shrink-0 rounded-md p-1 text-[#52525B] hover:bg-[#1A1A1F] hover:text-white"
        aria-label={es ? 'Cerrar' : 'Dismiss'}
        data-testid="studio-companion-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
